/**
 * CRM Integrations Index
 * Exports all CRM integration classes, factories, and registration functions
 */

// Internal imports for use within this module
import { registerSalesforceIntegration, unregisterSalesforceIntegration } from './registerSalesforceIntegration';
import { registerHubSpotIntegration, unregisterHubSpotIntegration } from './registerHubSpotIntegration';
import { registerPipedriveIntegration, unregisterPipedriveIntegration } from './registerPipedriveIntegration';
import { registerZohoCRMIntegration, unregisterZohoCRMIntegration } from './registerZohoCRMIntegration';
import { SalesforceIntegrationFactory } from './SalesforceIntegrationFactory';
import { HubSpotIntegrationFactory } from './HubSpotIntegrationFactory';
import { PipedriveIntegrationFactory } from './PipedriveIntegrationFactory';
import { ZohoCRMIntegrationFactory } from './ZohoCRMIntegrationFactory';

// Base CRM Integration
export { BaseCRMIntegration } from '../base/BaseCRMIntegration';
export type {
  CRMContact,
  CRMActivity,
  CRMSyncOptions,
  CRMSyncResult
} from '../base/BaseCRMIntegration';

// Salesforce Integration
export { SalesforceIntegration } from './SalesforceIntegration';
export { SalesforceIntegrationFactory } from './SalesforceIntegrationFactory';
export { registerSalesforceIntegration, unregisterSalesforceIntegration } from './registerSalesforceIntegration';

// HubSpot Integration
export { HubSpotIntegration } from './HubSpotIntegration';
export { HubSpotIntegrationFactory } from './HubSpotIntegrationFactory';
export { registerHubSpotIntegration, unregisterHubSpotIntegration } from './registerHubSpotIntegration';

// Pipedrive Integration
export { PipedriveIntegration } from './PipedriveIntegration';
export { PipedriveIntegrationFactory } from './PipedriveIntegrationFactory';
export { registerPipedriveIntegration, unregisterPipedriveIntegration } from './registerPipedriveIntegration';
export type {
  PipedriveConfig,
  PipedrivePerson,
  PipedriveDeal,
  PipedriveWebhookPayload
} from './PipedriveIntegration';

// Zoho CRM Integration
export { ZohoCRMIntegration } from './ZohoCRMIntegration';
export { ZohoCRMIntegrationFactory } from './ZohoCRMIntegrationFactory';
export { registerZohoCRMIntegration, unregisterZohoCRMIntegration } from './registerZohoCRMIntegration';
export type {
  ZohoCRMConfig,
  ZohoCRMTokens,
  ZohoContact,
  ZohoLead,
  ZohoWebhookPayload
} from './ZohoCRMIntegration';

/**
 * Register all CRM integrations
 */
export function registerAllCRMIntegrations(): void {
  try {
    registerSalesforceIntegration();
    registerHubSpotIntegration();
    registerPipedriveIntegration();
    registerZohoCRMIntegration();
    console.log('All CRM integrations registered successfully');
  } catch (error) {
    console.error('Error registering CRM integrations:', error);
    throw error;
  }
}

/**
 * Unregister all CRM integrations
 */
export function unregisterAllCRMIntegrations(): void {
  try {
    unregisterSalesforceIntegration();
    unregisterHubSpotIntegration();
    unregisterPipedriveIntegration();
    unregisterZohoCRMIntegration();
    console.log('All CRM integrations unregistered successfully');
  } catch (error) {
    console.error('Error unregistering CRM integrations:', error);
    throw error;
  }
}

/**
 * Get all available CRM integration types
 */
export function getAvailableCRMIntegrations() {
  return [
    {
      provider: 'salesforce',
      name: 'Salesforce',
      description: 'World\'s #1 CRM platform',
      authType: 'oauth2',
      capabilities: ['contacts', 'leads', 'opportunities', 'accounts', 'activities']
    },
    {
      provider: 'hubspot',
      name: 'HubSpot',
      description: 'Inbound marketing and sales platform',
      authType: 'oauth2',
      capabilities: ['contacts', 'companies', 'deals', 'tickets', 'activities']
    },
    {
      provider: 'pipedrive',
      name: 'Pipedrive',
      description: 'Sales-focused CRM for small and medium businesses',
      authType: 'api_key',
      capabilities: ['persons', 'organizations', 'deals', 'activities', 'notes']
    },
    {
      provider: 'zoho',
      name: 'Zoho CRM',
      description: 'Comprehensive CRM with extensive customization',
      authType: 'oauth2',
      capabilities: ['contacts', 'leads', 'accounts', 'deals', 'activities', 'custom_modules']
    }
  ];
}

/**
 * Create CRM integration factory by provider
 */
export function createCRMIntegrationFactory(provider: string) {
  switch (provider.toLowerCase()) {
    case 'salesforce':
      return SalesforceIntegrationFactory;
    case 'hubspot':
      return HubSpotIntegrationFactory;
    case 'pipedrive':
      return PipedriveIntegrationFactory;
    case 'zoho':
      return ZohoCRMIntegrationFactory;
    default:
      throw new Error(`Unknown CRM provider: ${provider}`);
  }
}

// Auto-register all CRM integrations when this module is imported
registerAllCRMIntegrations();